import React, { useCallback, useEffect, useRef, useState } from "react";
import { RotateCw } from "lucide-react";
import {
    addOperatorRequestMessage,
    closeOperatorRequest,
    fetchOperatorRequestAttachmentBlob,
    listOperatorRequests,
} from "../services/requests";
import FilterPopover, { DateRangeFields } from "../components/FilterPopover";
import { useWebSocket } from "../context/WebSocketContext";

function AuthImage({ fileUrl, alt }) {
    const [src, setSrc] = useState("");
    useEffect(() => {
        let revoked = false;
        let objectUrl = "";
        (async () => {
            try {
                const blob = await fetchOperatorRequestAttachmentBlob(fileUrl);
                if (revoked) return;
                objectUrl = URL.createObjectURL(blob);
                setSrc(objectUrl);
            } catch (_) {}
        })();
        return () => {
            revoked = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [fileUrl]);
    if (!src) return <div className="request-photo-skeleton" />;
    return <img src={src} alt={alt || "Фото"} />;
}

function PhotoStrip({ attachments }) {
    const list = Array.isArray(attachments) ? attachments : [];
    if (!list.length) return null;
    return (
        <div className="request-photo-strip">
            {list.map((att) => (
                <button
                    key={att.id}
                    type="button"
                    className="request-photo-thumb"
                    onClick={async () => {
                        try {
                            const blob = await fetchOperatorRequestAttachmentBlob(att.file_url);
                            const url = URL.createObjectURL(blob);
                            window.open(url, "_blank", "noopener,noreferrer");
                            setTimeout(() => URL.revokeObjectURL(url), 60_000);
                        } catch (_) {}
                    }}
                >
                    <AuthImage fileUrl={att.file_url} alt={att.file_name} />
                </button>
            ))}
        </div>
    );
}

const OperatorRequests = () => {
    const [status, setStatus] = useState("open");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [closing, setClosing] = useState({});
    const [replies, setReplies] = useState({});
    const [highlightedRequestId, setHighlightedRequestId] = useState(null);
    const [expandedRequestId, setExpandedRequestId] = useState(null);
    const requestRefs = useRef(new Map());
    const networkId = parseInt(localStorage.getItem("networkId"), 10);
    const { connect, subscribe, unsubscribe } = useWebSocket();

    const load = useCallback(async () => {
        setLoading(true);
        setMessage("");
        try {
            const data = await listOperatorRequests({
                status: status || undefined,
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
            });
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            setMessage("❌ " + (e?.message || "Не вдалося завантажити заявки"));
        } finally {
            setLoading(false);
        }
    }, [status, dateFrom, dateTo]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!Number.isFinite(networkId) || networkId <= 0) return;

        const roomId = `network:${networkId}`;
        const handleRequestChanged = (payload) => {
            const requestId = parseInt(payload?.id ?? payload?.request_id, 10);
            if (Number.isFinite(requestId) && requestId > 0) {
                try {
                    localStorage.setItem("pendingRequestOpenId_operator", String(requestId));
                } catch (_) {}
            }
            load();
        };

        connect("requests", roomId);
        subscribe("requests", roomId, "request_changed", handleRequestChanged);

        return () => {
            unsubscribe("requests", roomId, "request_changed", handleRequestChanged);
        };
    }, [connect, subscribe, unsubscribe, networkId, load]);

    useEffect(() => {
        let raw = null;
        try {
            raw = localStorage.getItem("pendingRequestOpenId_operator");
        } catch (_) {
            raw = null;
        }
        if (!raw) return;

        const id = parseInt(raw, 10);
        if (!Number.isFinite(id) || id <= 0) {
            try {
                localStorage.removeItem("pendingRequestOpenId_operator");
            } catch (_) {}
            return;
        }

        const item = (items || []).find((x) => Number(x?.id) === id);
        if (!item) {
            if (status) setStatus("");
            return;
        }

        try {
            localStorage.removeItem("pendingRequestOpenId_operator");
        } catch (_) {}

        setHighlightedRequestId(id);
        setExpandedRequestId(id);

        const el = requestRefs.current.get(id);
        if (el) {
            window.requestAnimationFrame(() => {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
            });
        }

        const timer = window.setTimeout(() => {
            setHighlightedRequestId((prev) => (prev === id ? null : prev));
        }, 60000);

        return () => window.clearTimeout(timer);
    }, [items, status]);

    const doClose = async (id) => {
        const comment = (closing[id] || "").trim();
        setLoading(true);
        setMessage("");
        try {
            await closeOperatorRequest({ requestId: id, operator_comment: comment || null });
            await load();
            setMessage("✅ Заявку закрито");
        } catch (e) {
            setMessage("❌ " + (e?.message || "Не вдалося закрити заявку"));
        } finally {
            setLoading(false);
        }
    };

    const doReply = async (id) => {
        const body = String(replies[id] || "").trim();
        if (!body) {
            setMessage("⚠️ Напишіть відповідь");
            return;
        }
        setLoading(true);
        setMessage("");
        try {
            await addOperatorRequestMessage(id, body);
            setReplies((prev) => ({ ...prev, [id]: "" }));
            await load();
            setMessage("✅ Відповідь надіслано");
        } catch (e) {
            setMessage("❌ " + (e?.message || "Не вдалося відповісти"));
        } finally {
            setLoading(false);
        }
    };

    const clearDates = () => {
        setDateFrom("");
        setDateTo("");
    };

    const toggleExpanded = (id) => {
        setExpandedRequestId((prev) => (prev === id ? null : id));
    };

    const getPreview = (text) => {
        const raw = String(text || "").replace(/\s+/g, " ").trim();
        if (!raw) return "Без опису";
        return raw.length > 120 ? `${raw.slice(0, 120)}...` : raw;
    };

    return (
        <div>
            <div className="config-header">
                <div className="header-buttons">
                    <button className="add-btn refresh toolbar-icon-btn" onClick={load} disabled={loading} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
                    </button>
                    <FilterPopover
                        active={Boolean(status !== "open" || dateFrom || dateTo)}
                        title="Фільтри заявок"
                        buttonTitle="Фільтри заявок"
                        onClear={() => {
                            setStatus("open");
                            clearDates();
                        }}
                    >
                        <label className="toolbar-filter-field">
                            <span>Статус</span>
                            <select value={status} onChange={(e) => setStatus(e.target.value)}>
                                <option value="open">Відкриті</option>
                                <option value="closed">Закриті</option>
                                <option value="">Усі</option>
                            </select>
                        </label>
                        <DateRangeFields
                            from={dateFrom}
                            to={dateTo}
                            onFromChange={setDateFrom}
                            onToChange={setDateTo}
                            fromLabel="Дата від"
                            toLabel="Дата до"
                        />
                    </FilterPopover>
                </div>
            </div>

            {message ? <p>{message}</p> : null}
            {loading ? <p>⏳ Завантаження...</p> : null}

            <div className="list-card">
                {items.length === 0 ? (
                    <p>Немає заявок</p>
                ) : (
                    <div className="operator-accordion-list">
                        {items.map((x) => {
                            const isExpanded = expandedRequestId === x.id;
                            const messages = Array.isArray(x.messages) ? x.messages : [];
                            const attachments = Array.isArray(x.attachments) ? x.attachments : [];
                            return (
                                <div
                                    key={x.id}
                                    ref={(el) => {
                                        if (el) requestRefs.current.set(x.id, el);
                                        else requestRefs.current.delete(x.id);
                                    }}
                                    className={`operator-accordion-item ${highlightedRequestId === x.id ? "is-highlighted" : ""}`}
                                    style={{ background: highlightedRequestId === x.id ? "#f8fbff" : undefined }}
                                >
                                    <button type="button" className="operator-accordion-head" onClick={() => toggleExpanded(x.id)}>
                                        <div className="operator-accordion-head-main">
                                            <div className="operator-accordion-title">
                                                #{x.id} {x.client_name || "Клієнт"}
                                                {x.apartment ? `, кв. ${x.apartment}` : ""}
                                            </div>
                                            <div className="operator-accordion-meta">
                                                <span>{x.status === "open" ? "Відкрита" : "Закрита"}</span>
                                                {x.closed_by === "client" ? <span>закрив клієнт</span> : null}
                                                {x.location_name ? <span>{x.location_name}</span> : <span>Загальна</span>}
                                                {attachments.length ? <span>📷 {attachments.length}</span> : null}
                                                {x.created_at ? <span>{new Date(x.created_at).toLocaleString()}</span> : null}
                                            </div>
                                            <div className="operator-accordion-preview">{getPreview(x.description)}</div>
                                        </div>
                                        <div className="operator-accordion-caret">{isExpanded ? "▾" : "▸"}</div>
                                    </button>

                                    {isExpanded ? (
                                        <div className="operator-accordion-body">
                                            <div className="operator-detail-grid">
                                                <div className="operator-detail-block operator-detail-block--full">
                                                    <span className="operator-detail-label">Опис заявки</span>
                                                    <div className="operator-detail-value">{x.description || "Без опису"}</div>
                                                </div>
                                                <div className="operator-detail-block">
                                                    <span className="operator-detail-label">Локація</span>
                                                    <div className="operator-detail-value">
                                                        {x.location_name || "Без привʼязки (загальна)"}
                                                    </div>
                                                </div>
                                                {attachments.length ? (
                                                    <div className="operator-detail-block operator-detail-block--full">
                                                        <span className="operator-detail-label">Фото</span>
                                                        <PhotoStrip attachments={attachments} />
                                                    </div>
                                                ) : null}
                                                {x.operator_comment ? (
                                                    <div className="operator-detail-block operator-detail-block--full">
                                                        <span className="operator-detail-label">Підсумок при закритті</span>
                                                        <div className="operator-detail-note">{x.operator_comment}</div>
                                                    </div>
                                                ) : null}
                                            </div>

                                            <div className="request-thread">
                                                <div className="request-thread__title">Переписка</div>
                                                {messages.length === 0 ? (
                                                    <div className="request-thread__empty">Поки немає коментарів</div>
                                                ) : (
                                                    messages.map((msg) => (
                                                        <div
                                                            key={msg.id}
                                                            className={`request-thread__bubble is-${msg.sender_type}`}
                                                        >
                                                            <div className="request-thread__meta">
                                                                {msg.sender_type === "operator" ? "Ви / оператор" : "Клієнт"}
                                                                {msg.created_at
                                                                    ? ` · ${new Date(msg.created_at).toLocaleString()}`
                                                                    : ""}
                                                            </div>
                                                            <div className="request-thread__body">{msg.body}</div>
                                                            <PhotoStrip attachments={msg.attachments} />
                                                        </div>
                                                    ))
                                                )}
                                            </div>

                                            {x.status === "open" ? (
                                                <div className="request-own-actions">
                                                    <label className="operator-detail-label">Відповідь клієнту</label>
                                                    <textarea
                                                        value={replies[x.id] || ""}
                                                        onChange={(e) => setReplies((p) => ({ ...p, [x.id]: e.target.value }))}
                                                        rows={3}
                                                        className="operator-toolbar-input"
                                                        style={{ width: "100%", minHeight: 80, resize: "vertical" }}
                                                        placeholder="Напишіть відповідь без закриття заявки..."
                                                    />
                                                    <div className="operator-stack-actions" style={{ marginTop: 8 }}>
                                                        <button className="add-btn" onClick={() => doReply(x.id)} disabled={loading}>
                                                            Надіслати відповідь
                                                        </button>
                                                    </div>

                                                    <div className="request-close-box" style={{ marginTop: 14 }}>
                                                        <label className="operator-detail-label">Закрити заявку</label>
                                                        <textarea
                                                            value={closing[x.id] || ""}
                                                            onChange={(e) => setClosing((p) => ({ ...p, [x.id]: e.target.value }))}
                                                            rows={3}
                                                            className="operator-toolbar-input"
                                                            style={{ width: "100%", minHeight: 80, resize: "vertical" }}
                                                            placeholder="Коментар при закритті (опціонально)"
                                                        />
                                                        <div className="operator-stack-actions" style={{ marginTop: 10 }}>
                                                            <button className="add-btn" onClick={() => doClose(x.id)} disabled={loading}>
                                                                Закрити заявку
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OperatorRequests;
